import React from 'react';
import { TrendingUp, AlertCircle, DollarSign, Users, ArrowRight } from 'lucide-react';
import { AIBadge } from './AIGlow';

/**
 * Next Best Action Component
 * Shows priority actions for the merchant to maximize revenue
 * Appears at the top of the dashboard
 * 
 * Updated: Added current price display, fixed click handlers for proper navigation behaviors
 */
export const NextBestAction = ({ actions = [], loading = false, onActionClick }) => {
  // Default actions if none provided
  // DISPLACED 2026-11-01 (fabricated-metric fix): the hardcoded sample actions below (invented products and
  // '+$N/week' revenue) were shown to any merchant with no real actions. Kept commented-out for reference only.
  // Restore check: only if replaced by real computed actions; verify NextBestAction.test.js still passes.
  // eslint-disable-next-line no-unused-vars
  const defaultActions = [
    {
      id: 1,
      type: 'create_deal',
      priority: 'high',
      icon: TrendingUp,
      title: 'Create deal for: Premium Wireless Headphones',
      description: 'High demand detected. Potential CI Score: 850',
      action: 'Create Deal',
      productId: 'product-123',
      currentPrice: 149.99,
      estimatedRevenue: '+$450/week',
    },
    {
      id: 2,
      type: 'adjust_pricing',
      priority: 'medium',
      icon: DollarSign,
      title: 'Adjust pricing on: Organic Skincare Set',
      description: 'Boost from CI 620 → 780 with 15% off',
      action: 'Adjust Pricing',
      dealId: 'deal-456',
      currentPrice: 89.99,
      estimatedRevenue: '+$280/week',
    },
    {
      id: 3,
      type: 'optimize_price',
      priority: 'medium',
      icon: AlertCircle,
      title: 'Optimize price on: Travel Backpacks',
      description: 'Current CI 450 → Target CI 750 ($5 reduction)',
      action: 'Optimize',
      productId: 'product-789',
      targetPrice: 44.99,
      currentPrice: 49.99,
      estimatedRevenue: '+$320/week',
    },
    {
      id: 4,
      type: 'creator_network',
      priority: 'low',
      icon: Users,
      title: 'Post to creators',
      description: '3 creators available in your category',
      action: 'View Creators',
      link: '/dashboard/creators',
      badge: 'Coming Soon',
    },
  ];

  // was: const displayActions = actions.length > 0 ? actions : defaultActions;
  const displayActions = actions;

  const handleActionClick = (action) => {
    if (onActionClick) {
      onActionClick(action);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#0E1426] to-[#1A2744] border border-[#22304A] rounded-xl p-6 animate-pulse">
        <div className="h-6 bg-[#22304A] rounded w-48 mb-4"></div>
        <div className="space-y-3">
          <div className="h-16 bg-[#22304A] rounded"></div>
          <div className="h-16 bg-[#22304A] rounded"></div>
        </div>
      </div>
    );
  }

  if (displayActions.length === 0) {
    return (
      <div
        className="rounded-xl border border-[#22304A] bg-[#0E1426] p-6"
        data-testid="next-best-action-empty"
      >
        <h2 className="text-xl font-bold text-white">Next Best Actions</h2>
        <p className="text-sm text-gray-400 mt-2">No recommendations yet. They appear here once we have measured data for your store.</p>
      </div>
    );
  }

  return (
    <div
      className="bg-gradient-to-br from-[#0E1426] to-[#1A2744] border border-[#22304A] rounded-xl shadow-lg"
      data-testid="next-best-action-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-[#22304A]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Next Best Actions</h2>
            <p className="text-sm text-gray-400">AI-powered recommendations to boost revenue</p>
          </div>
        </div>
        <AIBadge size="md" pulse={true} />
      </div>

      {/* Actions List */}
      <div className="divide-y divide-[#22304A]">
        {displayActions.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#3EF1C7]/10 flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">✨</span>
            </div>
            <p className="text-white font-medium">All actions completed!</p>
            <p className="text-sm text-gray-400 mt-1">Great job - you're all caught up</p>
          </div>
        ) : (
          displayActions.map((action) => (
            <div
              key={action.id}
              className={`p-4 transition-all duration-300 cursor-pointer group ${
                action.completed 
                  ? 'opacity-50 bg-[#8CF78E]/5 scale-95' 
                  : 'hover:bg-[#1A2744]/50'
              }`}
              onClick={() => !action.completed && handleActionClick(action)}
              data-testid={`next-action-${action.type}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  {/* Priority Indicator */}
                  <div
                    className={`w-1 h-12 rounded-full transition-colors ${
                      action.completed
                        ? 'bg-[#8CF78E]'
                        : action.priority === 'high'
                        ? 'bg-red-500'
                        : action.priority === 'medium'
                        ? 'bg-yellow-500'
                        : 'bg-gray-500'
                    }`}
                  />

                  {/* Icon */}
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      action.completed
                        ? 'bg-[#8CF78E]/10 text-[#8CF78E]'
                        : action.priority === 'high'
                        ? 'bg-red-500/10 text-red-400'
                        : action.priority === 'medium'
                        ? 'bg-yellow-500/10 text-yellow-400'
                        : 'bg-gray-500/10 text-gray-400'
                    }`}
                  >
                    {action.completed ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : action.iconType === 'trending' ? (
                      <TrendingUp className="w-5 h-5" />
                    ) : action.iconType === 'dollar' ? (
                      <DollarSign className="w-5 h-5" />
                    ) : action.iconType === 'alert' ? (
                      <AlertCircle className="w-5 h-5" />
                    ) : action.icon ? (
                      <action.icon className="w-5 h-5" />
                    ) : (
                      <TrendingUp className="w-5 h-5" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-medium transition-colors ${
                      action.completed 
                        ? 'text-[#8CF78E] line-through' 
                        : 'text-white group-hover:text-[#3EF1C7]'
                    }`}>
                      {action.completed ? '✓ Completed' : action.title}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {action.completed ? 'Refreshing recommendations...' : action.description}
                    </p>
                  </div>

                  {/* Current Price - Center */}
                  {action.currentPrice && !action.completed && (
                    <div className="px-4 text-center">
                      <p className="text-xs text-gray-500 uppercase">Current Price</p>
                      <p className="text-lg font-bold text-white">${action.currentPrice.toFixed(2)}</p>
                    </div>
                  )}
                </div>

                {/* Right Side */}
                {!action.completed && (
                  <div className="flex items-center gap-4 ml-4">
                    {action.estimatedRevenue && (
                      <span className="text-[#8CF78E] font-semibold whitespace-nowrap">{action.estimatedRevenue}</span>
                    )}
                    {action.badge && (
                      <span className="text-xs px-2 py-1 rounded-full bg-[#7A5CFF]/20 text-[#7A5CFF] whitespace-nowrap">
                        {action.badge}
                      </span>
                    )}
                    <button
                      className="px-4 py-2 rounded-lg bg-[#22304A] text-white text-sm font-medium hover:bg-[#3EF1C7] hover:text-[#0B1120] transition-colors flex items-center gap-2 whitespace-nowrap"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleActionClick(action);
                      }}
                    >
                      {action.action}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NextBestAction;
